// src/components/hooks/useEmpresaRegistro.ts

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

interface EmpresaForm {
  logo: File | null;
  empresa: string;
  id_categoria: string;
  subcategorias?: string[];
  descripcion: string;
  pais: string;
  ciudad: string;
  localidad: string;
  direccion: string;
  telefono: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type VerificarEmailResponse =
  | { ok: true; existente: boolean }
  | { ok: false; error: string };

export const CURRENT_POLICY_VERSION = 'v1.0-2025-09';
export const CURRENT_POLICY_TEXT =
  'Acepto el tratamiento de mis datos personales según la Política de Privacidad vigente.';

export const useEmpresaRegistro = () => {
  const [step, setStep] = useState(1);
  const [emailExists, setEmailExists] = useState(false);
  const [requirePassword, setRequirePassword] = useState(false);
  const [consentimiento, setConsentimiento] = useState(false);
  const [usuarioExistente, setUsuarioExistente] = useState<{
    id_usuario?: string;
    perfiles?: string;
  } | null>(null);

  /* ============================================================
     1) Verificar si el correo existe
  ============================================================ */
  const verificarEmail = useCallback(
    async (email: string): Promise<VerificarEmailResponse> => {
      const clean = email.trim().toLowerCase();

      const { data, error } = await supabase
        .from('vista_cliente_detalle')
        .select('id_usuario, email, perfiles')
        .eq('email', clean)
        .maybeSingle();

      if (error) {
        Sentry.captureException(error, { extra: { email: clean } });
        toast.error('Error verificando el correo.');
        return { ok: false, error: 'Error verificando el correo.' };
      }

      if (data) {
        setUsuarioExistente(data);
        setEmailExists(true);

        const tienePerfilEmpresa =
          data.perfiles && data.perfiles.toLowerCase().includes('empresa');

        if (tienePerfilEmpresa) {
          return {
            ok: false,
            error: 'El usuario ya tiene una empresa registrada.',
          };
        }

        return { ok: true, existente: true };
      }

      setRequirePassword(true);
      return { ok: true, existente: false };
    },
    []
  );

  /* ============================================================
     2) Crear usuario nuevo
  ============================================================ */
  const crearUsuarioNuevo = async (email: string, password: string) => {
    const clean = email.trim().toLowerCase();

    const { error: signUpError } = await supabase.auth.signUp({
      email: clean,
      password,
    });

    if (signUpError) {
      Sentry.captureException(signUpError, { extra: { email: clean } });
      return { ok: false, error: signUpError.message };
    }

    const { data: loginData, error: loginError } =
      await supabase.auth.signInWithPassword({ email: clean, password });

    if (loginError || !loginData.user) {
      Sentry.captureException(loginError, { extra: { email: clean } });
      return { ok: false, error: 'No se pudo iniciar sesión tras registro.' };
    }

    return { ok: true, id_usuario: loginData.user.id };
  };

  /* ============================================================
     3) Registrar empresa en la BD
  ============================================================ */
  const insertarEmpresa = async (
    empresaData: EmpresaForm,
    id_usuario: string
  ) => {
    const {
      empresa,
      id_categoria,
      descripcion,
      pais,
      ciudad,
      localidad,
      direccion,
      telefono,
      email,
    } = empresaData;

    const { data, error } = await supabase
      .from('empresas')
      .insert({
        empresa,
        id_categoria,
        descripcion,
        pais,
        ciudad,
        localidad,
        direccion,
        telefono,
        email: email.trim().toLowerCase(),
        id_usuario_propietario: id_usuario,
        usuario_creacion: id_usuario,
      })
      .select('id_empresa')
      .single();

    if (error) {
      Sentry.captureException(error, { extra: { id_usuario, empresa } });
      return { ok: false, error };
    }

    return { ok: true, id_empresa: data.id_empresa };
  };

  /* ============================================================
     4) Subir logo
  ============================================================ */
  const subirLogo = async (file: File, id_empresa: string) => {
    // El crop siempre produce PNG, forzamos la extensión para evitar
    // discrepancias entre file.name y el contentType real
    const path = `logos/${id_empresa}.png`;

    const { error: uploadError } = await supabase.storage
      .from('bookoll')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: 'image/png',
      });

    if (uploadError) {
      Sentry.captureException(uploadError, { extra: { id_empresa } });
      console.error('Error subiendo logo a Storage:', uploadError);
      return { ok: false, error: uploadError };
    }

    const { data } = supabase.storage.from('bookoll').getPublicUrl(path);

    const { error } = await supabase
      .from('empresas')
      .update({ logo: data.publicUrl })
      .eq('id_empresa', id_empresa);

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa } });
      console.error('Error actualizando URL del logo en BD:', error);
      return { ok: false, error };
    }

    return { ok: true };
  };

  /* ============================================================
     5) Asignar perfil empresa
  ============================================================ */
  const asignarPerfilEmpresa = async (
    id_usuario: string,
    id_empresa: string
  ) => {
    const { data: perfilData, error } = await supabase
      .from('perfiles')
      .select('id_perfil')
      .eq('perfil', 'empresa')
      .single();

    if (error) {
      Sentry.captureException(error, { extra: { id_usuario, id_empresa } });
      return { ok: false, error };
    }

    const { error: insertError } = await supabase
      .from('usuarios_perfiles')
      .insert({ id_usuario, id_perfil: perfilData.id_perfil, id_empresa });

    if (insertError) {
      Sentry.captureException(insertError, {
        extra: { id_usuario, id_empresa },
      });
      return { ok: false, error: insertError };
    }

    return { ok: true };
  };

  /* ============================================================
     5.1) Guardar subcategorías seleccionadas en el registro
     Igual que el logo: necesita ejecutarse DESPUÉS de asignarPerfilEmpresa,
     porque la política RLS de empresa_subcategoria usa is_empresa_admin,
     que solo es cierto una vez existe la fila en usuarios_perfiles.
  ============================================================ */
  const guardarSubcategoriasRegistro = async (
    id_empresa: string,
    subcategorias: string[]
  ) => {
    if (!subcategorias || subcategorias.length === 0) return { ok: true };

    const filas = subcategorias.map((id_subcategoria) => ({
      id_empresa,
      id_subcategoria,
    }));

    const { error } = await supabase
      .from('empresa_subcategoria')
      .upsert(filas, { onConflict: 'id_empresa,id_subcategoria' });

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa } });
      return { ok: false, error };
    }

    return { ok: true };
  };

  /* ============================================================
     6) Guardar consentimiento empresa
  ============================================================ */
  const guardarConsentimiento = async (
    id_usuario: string,
    id_empresa: string
  ) => {
    if (!consentimiento) return { ok: true };

    const { error } = await supabase.from('empresa_consentimientos').insert({
      id_empresa,
      acepta_politica_privacidad: true,
      acepta_terminos: true,
      acepta_comunicaciones: false,
      policy_version: CURRENT_POLICY_VERSION,
      policy_text: CURRENT_POLICY_TEXT,
      usuario_creacion: id_usuario,
    });

    if (error) {
      Sentry.captureException(error, { extra: { id_usuario, id_empresa } });
      return { ok: false, error };
    }

    return { ok: true };
  };

  /* ============================================================
     7) Registrar empresa COMPLETO (flujo principal)
  ============================================================ */
  const registrarEmpresaCompleto = async (
    empresaData: EmpresaForm,
    navigate: (path: string) => void
  ) => {
    const email = empresaData.email.trim().toLowerCase();

    // 1) Verificar email
    const verif = await verificarEmail(email);
    if (!verif.ok) return verif;

    // 2) Obtener / crear usuario autenticado
    let id_usuario: string | null = null;

    if (!verif.existente) {
      const nuevo = await crearUsuarioNuevo(email, empresaData.password);
      if (!nuevo.ok) return nuevo;
      id_usuario = nuevo.id_usuario ?? null;
    } else {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user?.id) {
        if (authError) Sentry.captureException(authError, { extra: { email } });
        return { ok: false, error: 'Usuario no autenticado.' };
      }

      id_usuario = user.id;
    }

    if (!id_usuario) return { ok: false, error: 'ID de usuario inválido.' };

    // 3) Comprobar si ya tiene empresa
    const { data: yaTieneEmpresa, error: empresaCheckError } = await supabase
      .from('empresas')
      .select('id_empresa')
      .eq('id_usuario_propietario', id_usuario)
      .maybeSingle();

    if (empresaCheckError) {
      Sentry.captureException(empresaCheckError, { extra: { id_usuario } });
      return { ok: false, error: empresaCheckError };
    }

    if (yaTieneEmpresa) {
      return {
        ok: false,
        error: 'El usuario ya tiene una empresa registrada.',
      };
    }

    /// 4) Insertar empresa
    const empresa = await insertarEmpresa(empresaData, id_usuario);
    if (!empresa.ok) return empresa;
    const id_empresa = empresa.id_empresa;

    // 5) Asignar perfil empresa  ← PRIMERO el perfil
    const perfil = await asignarPerfilEmpresa(id_usuario, id_empresa);
    if (!perfil.ok) return perfil;

    // 5.1) Guardar subcategorías seleccionadas (opcional, no bloqueante)
    if (empresaData.subcategorias && empresaData.subcategorias.length > 0) {
      const subcatResp = await guardarSubcategoriasRegistro(
        id_empresa,
        empresaData.subcategorias
      );
      if (!subcatResp.ok) {
        console.error(
          '[guardarSubcategoriasRegistro] error:',
          JSON.stringify(subcatResp.error)
        );
        toast.warning(
          'Las subcategorías no se pudieron guardar. Puedes añadirlas desde el panel.'
        );
      }
    }

    // 6) Subir logo  ← AHORA ya es is_empresa_admin → Storage lo permite
    if (empresaData.logo) {
      const logoResp = await subirLogo(empresaData.logo, id_empresa);
      if (!logoResp.ok) {
        console.error('[subirLogo] error:', JSON.stringify(logoResp.error));
        // No bloqueamos el registro, pero sí avisamos
        toast.warning(
          'El logo no se pudo guardar. Puedes subirlo desde el panel.'
        );
      }
    }

    // 7) Guardar consentimientos
    const consentimientoResp = await guardarConsentimiento(
      id_usuario,
      id_empresa
    );
    if (!consentimientoResp.ok) return consentimientoResp;

    // 8) Redirigir a panel empresa
    const { data: perfiles, error: perfilesError } = await supabase
      .from('vista_usuarios_detalle')
      .select('id_perfil, id_empresa, ruta_panel')
      .eq('id_usuario', id_usuario);

    if (perfilesError || !perfiles) {
      if (perfilesError)
        Sentry.captureException(perfilesError, { extra: { id_usuario } });
      return { ok: false, error: 'No se pudieron obtener los perfiles.' };
    }

    const perfilEmpresa = perfiles.find(
      (p) => p.ruta_panel === 'panel-empresa'
    );

    if (perfilEmpresa) {
      localStorage.setItem('id_usuario', id_usuario);
      localStorage.setItem('email', email);
      localStorage.setItem('id_perfil', perfilEmpresa.id_perfil);
      localStorage.setItem('id_empresa', perfilEmpresa.id_empresa);
      navigate(`/${perfilEmpresa.ruta_panel}`);
    }

    return { ok: true };
  };

  /* ============================================================
     RETURN DEL HOOK
  ============================================================ */
  return {
    step,
    setStep,
    emailExists,
    requirePassword,
    consentimiento,
    setConsentimiento,
    usuarioExistente,
    verificarEmail,
    registrarEmpresaCompleto,
  };
};